import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    GridItem,
    HStack,
    Input,
    Select,
    SimpleGrid,
    Stack,
    Text,
    useColorModeValue
} from "@chakra-ui/react";
import React, {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {withMask} from "use-mask-input"
import {ufs} from "../../../../utils";
import {boatService} from "../../../../services/boatService";

const PLATE_LENGTH = 7;

function BoatVehicleForm({onSubmit, onBack, values}) {

    const ids = useMemo(() => ({
        veiculos: "veiculos",
        placa: "placa",
        marcaModelo: "marcaModelo",
        tipo: "tipo",
        cor: "cor",
        ano: "ano",
        combustivel: "combustivel",
        uf: "uf",
        proprietario: "proprietario",
        numeroOcupantes: "numeroOcupantes",
        condutor: {
            key: "condutor",
            nome: "nome",
            cpf: "cpf",
            dataNascimento: "dataNascimento",
            endereco: "endereco",
            telefone: "telefone",
            cnh: "cnh",
            validadeCnh: "validadeCnh",
            categoriaCnh: "categoriaCnh"
        }
    }), []);

    const initFormData = (data) => {
        const initialFormData = {veiculos: []};

        if (data) {
            for (const v of data) {
                initialFormData.veiculos.push({
                    placa: v.placa?.trim(),
                    marcaModelo: v.marcaModelo?.trim(),
                    tipo: v.tipo?.trim(),
                    cor: v.cor?.trim(),
                    ano: v.ano?.trim(),
                    combustivel: v.combustivel?.trim(),
                    uf: v.uf?.trim(),
                    proprietario: v.proprietario?.trim(),
                    numeroOcupantes: v.numeroOcupantes?.trim(),
                    condutor: {
                        nome: v.condutor?.nome?.trim(),
                        dataNascimento: v.condutor?.dataNascimento?.trim(),
                        endereco: v.condutor?.endereco?.trim(),
                        cidade: v.condutor?.cidade?.trim(),
                        uf: v.condutor?.uf?.trim(),
                        telefone: v.condutor?.telefone?.trim(),
                        cpf: v.condutor?.cpf?.trim(),
                        cnh: v.condutor?.cnh?.trim(),
                        validadeCnh: v.condutor?.validadeCnh?.trim(),
                        categoriaCnh: v.condutor?.categoriaCnh?.trim(),
                    }
                });
            }
        }

        return initialFormData;
    };

    const {t} = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const [formData, setFormData] = useState(initFormData(values));

    const addVehicle = () => {
        formData.veiculos.push(
            {
                placa: '',
                marcaModelo: '',
                tipo: '',
                cor: '',
                ano: '',
                combustivel: '',
                uf: '',
                proprietario: '',
                numeroOcupantes: '',
                condutor: {
                    nome: '',
                    dataNascimento: '',
                    endereco: '',
                    cidade: '',
                    uf: '',
                    telefone: '',
                    cpf: '',
                    cnh: '',
                    validadeCnh: '',
                    categoriaCnh: ''
                }
            }
        );

        const newFormData = {...formData};
        setFormData(newFormData);
    }

    const removeVehicle = (index) => {
        formData.veiculos.splice(index, 1);

        const newFormData = {...formData};
        setFormData(newFormData);
    }

    const handleInputPlate = async (e, index) => {
        handleInputChange(e);

        const vehiclePlate = e.target.value;

        if (vehiclePlate?.length === PLATE_LENGTH) {
            try {
                const vehicle = await boatService.findVehicle(vehiclePlate);

                formData[ids.veiculos][index][ids.marcaModelo] = vehicle.descricaoMarcaModelo;
                formData[ids.veiculos][index][ids.tipo] = vehicle.descricaoTipo;
                formData[ids.veiculos][index][ids.cor] = vehicle.descricaoCor;
                formData[ids.veiculos][index][ids.ano] = vehicle.anoModelo + '';
                formData[ids.veiculos][index][ids.combustivel] = vehicle.descricaoCombustivel;
                formData[ids.veiculos][index][ids.uf] = vehicle.ufPlaca;
                formData[ids.veiculos][index][ids.proprietario] = vehicle.nomeProprietario;

                const newFormData = {...formData};
                setFormData(newFormData);
            } catch (error) {
                console.error(error);
            }
        }
    }

    const handleInputChange = (e) => {
        const idTokens = e.target.id.split("-");
        let value = e.target.value;

        if (idTokens.length === 4) {
            const index = idTokens[1];
            const innerKey = idTokens[2];
            const att = idTokens[3];

            formData.veiculos[index][innerKey][att] = value;

        } else {
            const index = idTokens[1];
            const att = idTokens[2];

            formData.veiculos[index][att] = value;
        }

        const newFormData = {...formData};
        setFormData(newFormData);
    };

    const handleOnSubmit = () => {
        onSubmit(formData);
    };

    return (
        <>
            <Button
                w="200px"
                variant="list-primary"
                fontSize="sm"
                onClick={addVehicle}
            >
                {t('boat.actions.add-vehicle')}
            </Button>

            <Stack>
                {formData?.veiculos?.map((v, index) => (
                    <SimpleGrid key={`vehicles-${index}`} mt="10px">
                        <HStack>
                            <Text fontWeight="bold">{`${t('boat.new.vehicle')} ${index + 1}`}</Text>
                            <Button variant="light" onClick={() => removeVehicle(index)}>{t('system.actions.remove')}</Button>
                        </HStack>

                        <SimpleGrid spacing={2} columns={{sm: 1, md: 3}} mt="10px">
                            <FormControl isRequired isInvalid={v[ids.placa].length === 0}>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.plate")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.placa]}`}
                                    value={v[ids.placa]}
                                    onChange={(e) => {
                                        e.target.value = e.target.value.toUpperCase();
                                        handleInputPlate(e, index);
                                    }}
                                    placeholder={t('boat.new.plate')}
                                    _placeholder={{color: placeholderColor}}
                                    maxLength={PLATE_LENGTH}
                                />
                                <FormErrorMessage>
                                    {v[ids.placa].length === 0 ? 'Campo obrigatório' : ""}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {`${t("boat.new.brand")}/${t("boat.new.model")}`}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.marcaModelo]}`}
                                    value={v[ids.marcaModelo]}
                                    onChange={handleInputChange}
                                    placeholder={`${t("boat.new.brand")}/${t("boat.new.model")}`}
                                    _placeholder={{color: placeholderColor}}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.type")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.tipo]}`}
                                    value={v[ids.tipo]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.type")}
                                    _placeholder={{color: placeholderColor}}
                                />
                            </FormControl>
                        </SimpleGrid>

                        <SimpleGrid spacing={2} columns={{sm: 1, md: 2}} mt="10px">
                            <SimpleGrid spacing={2} columns={{sm: 1, md: 3}}>
                                <GridItem colSpan={2}>
                                    <FormControl>
                                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                            {t("boat.new.color")}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            variant="main"
                                            id={`${[ids.veiculos]}-${index}-${[ids.cor]}`}
                                            value={v[ids.cor]}
                                            onChange={handleInputChange}
                                            placeholder={t("boat.new.color")}
                                            _placeholder={{color: placeholderColor}}
                                        />
                                    </FormControl>
                                </GridItem>
                                <FormControl>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("boat.new.year")}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        id={`${[ids.veiculos]}-${index}-${[ids.ano]}`}
                                        value={v[ids.ano]}
                                        onChange={handleInputChange}
                                        placeholder={t("boat.new.year")}
                                        _placeholder={{color: placeholderColor}}
                                        ref={withMask("9999")}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <SimpleGrid spacing={2} columns={{sm: 1, md: 3}}>
                                <GridItem colSpan={2}>
                                    <FormControl>
                                        <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                            {t("boat.new.fuel")}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            variant="main"
                                            id={`${[ids.veiculos]}-${index}-${[ids.combustivel]}`}
                                            value={v[ids.combustivel]}
                                            onChange={handleInputChange}
                                            placeholder={t("boat.new.fuel")}
                                            _placeholder={{color: placeholderColor}}
                                        />
                                    </FormControl>
                                </GridItem>
                                <FormControl>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`boat.new.state`)}
                                    </FormLabel>
                                    <Select
                                        fontSize="sm"
                                        variant="main"
                                        id={`${[ids.veiculos]}-${index}-${[ids.uf]}`}
                                        value={v[ids.uf]}
                                        onChange={handleInputChange}
                                        placeholder={t(`boat.new.state`)}
                                    >
                                        {ufs.map((uf) => (
                                            <option key={uf.value} value={uf.value}>
                                                {uf.value} - {uf.label}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </SimpleGrid>
                        </SimpleGrid>

                        <SimpleGrid spacing={2} columns={{sm: 1, md: 5}} mt="10px">
                            <GridItem colSpan={4}>
                                <FormControl>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("boat.new.owner")}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        id={`${[ids.veiculos]}-${index}-${[ids.proprietario]}`}
                                        value={v[ids.proprietario]}
                                        onChange={handleInputChange}
                                        placeholder={t("boat.new.owner")}
                                        _placeholder={{color: placeholderColor}}
                                    />
                                </FormControl>
                            </GridItem>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.number-people")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    type="number"
                                    id={`${[ids.veiculos]}-${index}-${[ids.numeroOcupantes]}`}
                                    value={v[ids.numeroOcupantes]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.number-people")}
                                    _placeholder={{color: placeholderColor}}
                                />
                            </FormControl>
                        </SimpleGrid>


                        <SimpleGrid spacing={2} columns={{sm: 1, md: 5}} mt="10px">
                            <GridItem colSpan={3}>
                                <FormControl>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("boat.new.driver.title")}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        variant="main"
                                        id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.nome]}`}
                                        value={v[ids.condutor.key][ids.condutor.nome]}
                                        onChange={handleInputChange}
                                        placeholder={t("boat.new.driver.title")}
                                        _placeholder={{color: placeholderColor}}
                                    />
                                </FormControl>
                            </GridItem>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.id")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.cpf]}`}
                                    value={v[ids.condutor.key][ids.condutor.cpf]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.id")}
                                    _placeholder={{color: placeholderColor}}
                                    ref={withMask("999.999.999-99")}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.birthdate")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.dataNascimento]}`}
                                    value={v[ids.condutor.key][ids.condutor.dataNascimento]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.birthdate")}
                                    _placeholder={{color: placeholderColor}}
                                    ref={withMask("99/99/9999")}
                                />
                            </FormControl>
                        </SimpleGrid>

                        <SimpleGrid spacing={2} columns={1} mt="10px">
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.address")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.endereco]}`}
                                    value={v[ids.condutor.key][ids.condutor.endereco]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.address")}
                                    _placeholder={{color: placeholderColor}}
                                />
                            </FormControl>
                        </SimpleGrid>

                        <SimpleGrid spacing={2} columns={{sm: 1, md: 4}} mt="10px">
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.phone")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.telefone]}`}
                                    value={v[ids.condutor.key][ids.condutor.telefone]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.phone")}
                                    _placeholder={{color: placeholderColor}}
                                    ref={withMask("(99) [9]9999-9999")}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.license")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.cnh]}`}
                                    value={v[ids.condutor.key][ids.condutor.cnh]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.license")}
                                    _placeholder={{color: placeholderColor}}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.expiration")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.validadeCnh]}`}
                                    value={v[ids.condutor.key][ids.condutor.validadeCnh]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.expiration")}
                                    _placeholder={{color: placeholderColor}}
                                    ref={withMask("99/99/9999")}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                    {t("boat.new.driver.category")}
                                </FormLabel>
                                <Input
                                    fontSize="sm"
                                    variant="main"
                                    id={`${[ids.veiculos]}-${index}-${[ids.condutor.key]}-${[ids.condutor.categoriaCnh]}`}
                                    value={v[ids.condutor.key][ids.condutor.categoriaCnh]}
                                    onChange={handleInputChange}
                                    placeholder={t("boat.new.driver.category")}
                                    _placeholder={{color: placeholderColor}}
                                />
                            </FormControl>
                        </SimpleGrid>

                    </SimpleGrid>
                ))}
            </Stack>

            <Flex w="100%" justify="space-between" mt="20px">
                <Button
                    variant="light"
                    fontSize="sm"
                    alignSelf="flex-end"
                    w="125px"
                    onClick={onBack}
                >
                    {t("system.actions.back")}
                </Button>
                <Button
                    w="125px"
                    variant="dark"
                    fontSize="sm"
                    onClick={handleOnSubmit}
                >
                    {t('system.actions.next')}
                </Button>
            </Flex>
        </>
    )
}

export default BoatVehicleForm;